import { Switch, Route, Redirect } from "wouter";
import { useEffect } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import ProtectedRoute from "@/components/ProtectedRoute";
import { NotificationToastProvider } from "@/components/ui/NotificationToast";
import ErrorBoundary from "@/components/ui/ErrorBoundary";
import NotFound from "@/pages/not-found";
import Dashboard from "@/pages/dashboard";
import Organizations from "@/pages/corporate/organizations";
import OrganizationDetail from "@/pages/corporate/organizations/detail";
import BookingManagement from "@/pages/bookings";
import BookingDetailPage from "@/pages/BookingDetail";
import BookingMarketplacePage from "@/pages/BookingMarketplace";
import BookingMarketplaceServicePage from "@/pages/BookingMarketplaceService";
import Vendors from "@/pages/Vendors";
import MarketplacePage from "@/pages/MarketplaceAdmin/MarketplacePage";
import TenantCustomizations from "@/pages/tenant-customizations";
import LocalizationDashboard from "@/pages/LocalizationDashboard";
import FinancialOperations from "@/pages/FinancialOperations";
import BillingOverviewPage from "@/pages/Billing/OverviewPage";
import ServiceCategories from "@/pages/service-categories";
import PerksPage from "@/pages/Perks";
import PerkDetail from "@/pages/Perks/PerkDetail";
import CreatePerk from "@/pages/Perks/CreatePerk";
import EditPerk from "@/pages/Perks/EditPerk";
import PerkCategoriesPage from "@/pages/Perks/PerkCategories";
import PerkLocationsPage from "@/pages/Perks/PerkLocations";
import StickersPage from "@/pages/feed/Stickers";
import KudosTypesPage from "@/pages/feed/KudosTypes";
import ActivationTemplatesPage from "@/pages/activations/ActivationTemplatesPage";
import ActivationTemplateEditorPage from "@/pages/activations/ActivationTemplateEditorPage";
import ActivationEventsPage from "@/pages/activations/ActivationEventsPage";
import ActivationEventDetailPage from "@/pages/activations/ActivationEventDetailPage";
import ActivationResultsPage from "@/pages/activations/ActivationResultsPage";
import OperatorConsolePage from "@/pages/activations/OperatorConsolePage";
import LeadsPage from "@/pages/activations/LeadsPage";
import DesignSystem from "@/pages/DesignSystem";
import AccountDeletionsPage from "@/pages/AccountDeletions";
import EngagementScorePage from "@/pages/engagement/EngagementScorePage";
import AppFeedbackPage from "@/pages/app-feedback/AppFeedbackPage";
import JoinLinksPage from "@/pages/join-links/JoinLinksPage";
import AuthCallback from "@/pages/auth/callback";
import Shell from "@/components/layout/Shell";
import RequireMenuAccess from "@/components/auth/RequireMenuAccess";
import BackendPendingGate from "@/components/BackendPendingGate";
import { tokenRefreshService } from "@/services/tokenRefresh";
import { useAuth } from "@/hooks/useAuth";

function Router() {
  const { user, isAuthenticated } = useAuth();

  // Initialize token refresh service when user is authenticated via SSO
  useEffect(() => {
    const authMethod = localStorage.getItem('auth_method');

    if (isAuthenticated && user && authMethod === 'Entra') {
      console.log('Starting proactive token refresh for SSO user');
      tokenRefreshService.startProactiveRefresh();
    }

    return () => {
      // Cleanup on unmount
      tokenRefreshService.stopProactiveRefresh();
    };
  }, [isAuthenticated, user]);

  return (
    <Shell>
      <Switch>
          {/* Landing: `/` redirects to the ungated Organizations list (both roles land somewhere real).
              MUST wrap in <Route path="/"> — a bare <Redirect> in a <Switch> is a pathless catch-all
              that would swallow every route. `replace` avoids trapping the Back button. */}
          <Route path="/">
            <Redirect to="/organizations" replace />
          </Route>
          <Route path="/dashboard">
            <BackendPendingGate menuId="dashboard" title="Dashboard">
              <Dashboard />
            </BackendPendingGate>
          </Route>
          <Route path="/auth/callback" component={AuthCallback} />
          <Route path="/organizations" component={Organizations} />
          <Route path="/organizations/:organizationId" component={OrganizationDetail} />
          <Route path="/bookings/:bookingId/marketplace/services/:serviceId" component={BookingMarketplaceServicePage} />
          <Route path="/bookings/:bookingId/marketplace" component={BookingMarketplacePage} />
          <Route path="/bookings/:bookingId" component={BookingDetailPage} />
          <Route path="/bookings" component={BookingManagement} />
          <Route path="/vendors" component={Vendors} />
          <Route path="/perks/create" component={CreatePerk} />
          <Route path="/perks/:perkId/edit" component={EditPerk} />
          <Route path="/perks/:perkId" component={PerkDetail} />
          <Route path="/perks" component={PerksPage} />
          <Route path="/perk-categories" component={PerkCategoriesPage} />
          <Route path="/perk-locations" component={PerkLocationsPage} />
          <Route path="/feed-stickers" component={StickersPage} />
          <Route path="/kudos-types" component={KudosTypesPage} />
          {/* Activations — Athlyn-team only (gated via RequireMenuAccess menuId="activations"). */}
          <Route path="/activations/templates/new">
            <RequireMenuAccess menuId="activations">
              <ActivationTemplateEditorPage />
            </RequireMenuAccess>
          </Route>
          <Route path="/activations/templates/:templateId">
            <RequireMenuAccess menuId="activations">
              <ActivationTemplateEditorPage />
            </RequireMenuAccess>
          </Route>
          <Route path="/activations/templates">
            <RequireMenuAccess menuId="activations">
              <ActivationTemplatesPage />
            </RequireMenuAccess>
          </Route>
          <Route path="/activations/events/:eventId/results">
            <RequireMenuAccess menuId="activations">
              <ActivationResultsPage />
            </RequireMenuAccess>
          </Route>
          <Route path="/activations/events/:eventId/operator">
            <RequireMenuAccess menuId="activations">
              <OperatorConsolePage />
            </RequireMenuAccess>
          </Route>
          <Route path="/activations/events/:eventId/leads">
            <RequireMenuAccess menuId="activations">
              <LeadsPage />
            </RequireMenuAccess>
          </Route>
          <Route path="/activations/events/:eventId">
            <RequireMenuAccess menuId="activations">
              <ActivationEventDetailPage />
            </RequireMenuAccess>
          </Route>
          <Route path="/activations/events">
            <RequireMenuAccess menuId="activations">
              <ActivationEventsPage />
            </RequireMenuAccess>
          </Route>
          <Route path="/marketplace" component={MarketplacePage} />
          <Route path="/tenant-customizations">
            <RequireMenuAccess menuId="customizations">
              <BackendPendingGate menuId="customizations" title="Tenant Customizations">
                <TenantCustomizations />
              </BackendPendingGate>
            </RequireMenuAccess>
          </Route>
          <Route path="/localization">
            <RequireMenuAccess menuId="localization">
              <BackendPendingGate menuId="localization" title="Localization Dashboard">
                <LocalizationDashboard />
              </BackendPendingGate>
            </RequireMenuAccess>
          </Route>
          <Route path="/financial-operations">
            <BackendPendingGate menuId="financial" title="Financial Operations">
              <FinancialOperations />
            </BackendPendingGate>
          </Route>
          <Route path="/billing">
            <RequireMenuAccess menuId="billing">
              <BackendPendingGate menuId="billing" title="Global Billing Dashboard">
                <BillingOverviewPage />
              </BackendPendingGate>
            </RequireMenuAccess>
          </Route>
          {/* /analytics — page file (AnalyticsBI) deleted; gate renders with no children. */}
          <Route path="/analytics">
            <BackendPendingGate menuId="analytics" title="Analytics & BI" />
          </Route>
          {/* /system-administration — page file (SystemAdministration) deleted; gate has no children. */}
          <Route path="/system-administration">
            <RequireMenuAccess menuId="admin">
              <BackendPendingGate menuId="admin" title="System Administration" />
            </RequireMenuAccess>
          </Route>
          <Route path="/service-categories" component={ServiceCategories} />
          <Route path="/design-system">
            <RequireMenuAccess menuId="design-system">
              <DesignSystem />
            </RequireMenuAccess>
          </Route>
          {/* Account deletions — support deletes a guest's account; Athlyn staff (Admin + Team) only. */}
          <Route path="/account-deletions">
            <RequireMenuAccess menuId="account-deletions">
              <AccountDeletionsPage />
            </RequireMenuAccess>
          </Route>
          {/* Engagement score (admins edit, team reads) + App feedback (platform-wide, staff only) — ps-cms-engagement. */}
          <Route path="/engagement-score">
            <RequireMenuAccess menuId="engagement">
              <EngagementScorePage />
            </RequireMenuAccess>
          </Route>
          <Route path="/app-feedback">
            <RequireMenuAccess menuId="app-feedback">
              <AppFeedbackPage />
            </RequireMenuAccess>
          </Route>
          {/* Join links — every company's app download link, opens, joins, seats and QR (staff only) — join-links-cms. */}
          <Route path="/join-links">
            <RequireMenuAccess menuId="join-links">
              <JoinLinksPage />
            </RequireMenuAccess>
          </Route>
          <Route component={NotFound} />
        </Switch>
    </Shell>
  );
}

function App() {
  return (
    <ErrorBoundary level="page" showErrorDetails={true}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <ProtectedRoute>
            <NotificationToastProvider>
              <ErrorBoundary level="section">
                <Router />
              </ErrorBoundary>
            </NotificationToastProvider>
          </ProtectedRoute>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
